// Spreadsheet engine for the pawn "Import Data" screen.
//
// One dataset (loans, receipts, settlements…) = one spec in ./importSpecs. This
// module is what turns any such spec into a real workbook and reads a filled one
// back: build the template, pick the data sheet, match the columns, check every
// cell against its declared type, and hand the screen a row-by-row verdict.
//
// Deliberately free of React/MUI — the dialog owns the UI, this owns "what does
// the spreadsheet mean". A spec's `columns` array is the single source of truth:
// it generates the template AND drives the parser, so the two cannot drift.
//
// The shape was lifted from ../pawnCustomer/bulkCustomerSheet (the proven
// customer import) and generalised over the spec. The genuinely shared cell
// helpers — text, dates, mobiles — are imported from there rather than copied,
// so a fix to a date format fixes every import at once.
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  txt,
  toISODate,
  normalizeMobile,
} from "../pawnCustomer/bulkCustomerSheet";

// ─── Sheet names ──────────────────────────────────────────────────────────────
// The sheet the operator types into. Every template carries it, and it is the
// first place the parser looks.
export const DATA_SHEET_NAME = "Ready To Fill";

// Sheets that must never be read as data, whatever their position.
const NON_DATA_SHEETS = ["column guide", "notes", "sample data"];

// Rows the template itself writes under the header of its "Template" sheet. If
// somebody fills in and uploads that sheet, these must not become records.
const GUARD_PREFIXES = [
  "what to fill:",
  "example:",
  "mandatory:",
  "(excel column",
];

// ─── Small helpers ────────────────────────────────────────────────────────────
const normKey = (v) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// 0 → A, 25 → Z, 26 → AA … so a spec never hand-numbers its own columns and
// inserting one in the middle re-letters everything after it for free.
export const columnLetter = (index) => {
  let n = index;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
};

/** Stamps each column with the Excel letter its position gives it. */
export const withLetters = (columns) =>
  columns.map((c, i) => ({ ...c, letter: columnLetter(i) }));

export const headersOf = (spec) => spec.columns.map((c) => c.header);

/** Column letter for a field, so a message can name it without counting. */
export const colOf = (spec, key) =>
  spec.columns.find((c) => c.key === key)?.letter || "?";

export const mandatoryCount = (spec) =>
  spec.columns.filter((c) => c.mandatory).length;

const autoFitCols = (rows, min = 10, max = 60) =>
  (rows[0] || []).map((_, colIdx) => ({
    wch: Math.min(
      max,
      Math.max(min, ...rows.map((r) => String(r?.[colIdx] ?? "").length + 2))
    ),
  }));

// A spec writes its samples as objects keyed by column key, so adding a column
// can never silently shift the sample values one cell to the left.
const sampleAoA = (spec) =>
  (spec.sampleRows || []).map((row) =>
    spec.columns.map((c) => row[c.key] ?? "")
  );

// ─── Template workbook ────────────────────────────────────────────────────────
/**
 * Builds and downloads the template for one dataset. Five sheets:
 *   Template      — headers with their documentation stacked under each one
 *   Column Guide  — the same contract as a readable table
 *   Notes         — the rules of this particular import
 *   Sample Data   — a worked example to copy from (never imported)
 *   Ready To Fill — the header row only, so the first data row is row 2
 */
export const downloadTemplate = (spec) => {
  const wb = XLSX.utils.book_new();
  const headers = headersOf(spec);

  // 1 · Template — the answer to "what goes in this column?" sits IN the column.
  const templateRows = [
    headers,
    spec.columns.map((c) => `WHAT TO FILL: ${c.whatToFill}`),
    spec.columns.map((c) => `EXAMPLE: ${c.example ?? ""}`),
    spec.columns.map((c) => `MANDATORY: ${c.mandatory ? "YES" : "No"}`),
    spec.columns.map((c) => `(Excel column ${c.letter})`),
  ];
  const wsTemplate = XLSX.utils.aoa_to_sheet(templateRows);
  wsTemplate["!cols"] = autoFitCols([headers], 16, 28);
  XLSX.utils.book_append_sheet(wb, wsTemplate, "Template");

  // 2 · Column Guide.
  const guideRows = [
    [
      "Excel Column",
      "Header",
      "What to fill",
      "Example",
      "Mandatory",
      "Where this data goes",
    ],
    ...spec.columns.map((c) => [
      c.letter,
      c.header,
      c.whatToFill,
      c.example ?? "",
      c.mandatory ? "YES" : "No",
      c.goesTo ?? "",
    ]),
  ];
  const wsGuide = XLSX.utils.aoa_to_sheet(guideRows);
  wsGuide["!cols"] = [
    { wch: 13 },
    { wch: 26 },
    { wch: 78 },
    { wch: 30 },
    { wch: 11 },
    { wch: 58 },
  ];
  XLSX.utils.book_append_sheet(wb, wsGuide, "Column Guide");

  // 3 · Notes — numbered here rather than in the spec so inserting a rule does
  //     not mean renumbering the rest by hand.
  const noteLines = [
    [spec.notesTitle || `How the ${spec.title} import works`],
    [""],
    [`Screen: Pawn module > Import Data > ${spec.title}`],
    [`One row = ${spec.rowMeaning}`],
    [""],
    ...(spec.notes || []).map((line, i) => [`${i + 1}. ${line}`]),
  ];
  const wsNotes = XLSX.utils.aoa_to_sheet(noteLines);
  wsNotes["!cols"] = [{ wch: 155 }];
  XLSX.utils.book_append_sheet(wb, wsNotes, "Notes");

  // 4 · Sample Data.
  const samples = sampleAoA(spec);
  const sampleRows = [headers, ...samples];
  const wsSample = XLSX.utils.aoa_to_sheet(sampleRows);
  wsSample["!cols"] = autoFitCols(sampleRows, 12, 46);
  XLSX.utils.book_append_sheet(wb, wsSample, "Sample Data");

  // 5 · Ready To Fill.
  const wsFill = XLSX.utils.aoa_to_sheet([headers]);
  wsFill["!cols"] = autoFitCols(sampleRows, 12, 46);
  XLSX.utils.book_append_sheet(wb, wsFill, DATA_SHEET_NAME);

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    spec.fileName
  );
};

// ─── File → workbook ──────────────────────────────────────────────────────────
export const readWorkbook = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = (e) => {
      try {
        resolve(
          XLSX.read(new Uint8Array(e.target.result), {
            type: "array",
            cellDates: true,
          })
        );
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });

// ─── Workbook → rows ──────────────────────────────────────────────────────────
// Picks the sheet to read, drops the header/guide rows, and keeps the true Excel
// row number of everything that survives so a problem can be pointed at.
export const extractRows = (workbook, spec) => {
  const headerMatchMinimum = Math.max(2, Math.ceil(spec.columns.length / 4));
  const lower = (n) => n.trim().toLowerCase();

  const candidates = workbook.SheetNames.filter(
    (n) => !NON_DATA_SHEETS.includes(lower(n))
  );
  const ordered = [
    ...candidates.filter((n) => lower(n) === DATA_SHEET_NAME.toLowerCase()),
    ...candidates.filter((n) => lower(n) === "template"),
    ...candidates.filter(
      (n) =>
        lower(n) !== DATA_SHEET_NAME.toLowerCase() && lower(n) !== "template"
    ),
  ];

  for (const sheetName of ordered) {
    const ws = workbook.Sheets[sheetName];
    if (!ws || !ws["!ref"]) continue;

    const firstRow = XLSX.utils.decode_range(ws["!ref"]).s.r;
    const aoa = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      blankrows: true,
      raw: true,
      defval: null,
    });

    let headerIndexes = null; // column key -> position, when headers are readable
    let headerRowConsidered = false;
    const dataRows = [];

    aoa.forEach((row, idx) => {
      const cells = Array.isArray(row) ? row : [];
      const rowNo = firstRow + idx + 1; // 1-based, as Excel shows it

      if (cells.every((c) => txt(c) === "")) return;

      const firstFilled = txt(cells.find((c) => txt(c) !== ""));
      if (GUARD_PREFIXES.some((p) => firstFilled.toLowerCase().startsWith(p)))
        return;

      // The header can only be the FIRST non-empty, non-guard row, and it has to
      // look like one — a fair share of the columns matching by name. Both
      // halves matter: a data row that happens to hold one column's name would
      // otherwise be swallowed as a header and the file reported as empty.
      if (!headerRowConsidered) {
        headerRowConsidered = true;
        const found = {};
        cells.forEach((cell, colIdx) => {
          const hit = spec.columns.find(
            (c) => normKey(c.header) === normKey(cell)
          );
          if (hit && found[hit.key] === undefined) found[hit.key] = colIdx;
        });
        if (Object.keys(found).length >= headerMatchMinimum) {
          headerIndexes = found;
          return;
        }
      }

      dataRows.push({ rowNo, cells });
    });

    if (dataRows.length) return { sheetName, headerIndexes, dataRows };
  }

  return { sheetName: null, headerIndexes: null, dataRows: [] };
};

// ─── Cell checks ──────────────────────────────────────────────────────────────
const YES = /^(y|yes|true|1|on)$/;
const NO = /^(n|no|false|0|off)$/;

export const toNumber = (v) => {
  const raw = txt(v).replace(/[₹,\s]/g, "");
  if (raw === "") return { ok: true, value: null };
  const n = Number(raw);
  if (!isFinite(n)) return { ok: false, reason: "is not a number" };
  return { ok: true, value: n };
};

/**
 * Runs one cell through its column's declared type. Returns
 * `{ value, problem }` — `value` is the normalised value an importer would send
 * (null when blank) and `problem` is a sentence naming the column, or null.
 *
 * Mandatory-ness is NOT checked here: only the caller knows whether the row is a
 * continuation of the one above and may inherit the value.
 */
const checkCell = (col, raw) => {
  const asText = txt(raw);

  // A cell still holding its own column's name means the header row was copied
  // down, or a placeholder was never replaced. Treat it as blank, and say so.
  if (asText && normKey(asText) === normKey(col.header))
    return {
      value: null,
      problem: `${col.header} still holds the column name — leave it blank instead`,
    };

  if (asText === "") return { value: null, problem: null };

  switch (col.type) {
    case "date": {
      const parsed = toISODate(raw);
      return parsed.ok
        ? { value: parsed.value, problem: null }
        : { value: null, problem: `${col.header} ${parsed.reason}` };
    }
    case "number": {
      const parsed = toNumber(raw);
      if (!parsed.ok)
        return { value: null, problem: `${col.header} ${parsed.reason}` };
      if (parsed.value < 0)
        return { value: null, problem: `${col.header} cannot be negative` };
      return { value: parsed.value, problem: null };
    }
    case "mobile": {
      const digits = normalizeMobile(raw);
      return digits.length === 10
        ? { value: digits, problem: null }
        : {
            value: null,
            problem: `${col.header} must be exactly 10 digits (read "${asText}")`,
          };
    }
    case "yesno": {
      const key = normKey(asText);
      if (YES.test(key)) return { value: true, problem: null };
      if (NO.test(key)) return { value: false, problem: null };
      return {
        value: null,
        problem: `${col.header} must be Yes or No (read "${asText}")`,
      };
    }
    case "list": {
      const options = col.options || [];
      const hit = options.find((o) => normKey(o) === normKey(asText));
      return hit
        ? { value: hit, problem: null }
        : {
            value: asText,
            problem: `${col.header} should be one of ${options.join(
              " / "
            )} (read "${asText}")`,
          };
    }
    default:
      return {
        value: col.upper ? asText.toUpperCase() : asText,
        problem: null,
      };
  }
};

// ─── Rows → checked records ───────────────────────────────────────────────────
/**
 * Turns the extracted rows into the list the dialog renders.
 *
 * Grouped datasets (a loan and its several ornaments) are why this is not a
 * plain map: a row whose group column repeats the row above — or is left blank —
 * is a CONTINUATION of it, so the record-level columns are inherited and their
 * mandatory-ness is not asked of that row again. Only `scope: "item"` columns
 * have to be filled on a continuation row.
 */
export const buildRows = ({ headerIndexes, dataRows }, spec) => {
  const cellAt = (cells, col, colIdx) =>
    headerIndexes ? cells[headerIndexes[col.key] ?? -1] : cells[colIdx];

  let lastGroup = null;

  return dataRows.map(({ rowNo, cells }) => {
    const values = {};
    // What the operator actually typed, kept beside the normalised value so a
    // cell that could not be read can still be shown back to them — "—" is no
    // help at all when the complaint is "that is not a date".
    const raw = {};
    const problems = [];

    // Group first — whether this row continues the previous record decides
    // which mandatory columns apply to it.
    let group = null;
    let isContinuation = false;
    if (spec.groupBy) {
      const groupCol = spec.columns.find((c) => c.key === spec.groupBy);
      const groupIdx = spec.columns.indexOf(groupCol);
      const raw = txt(cellAt(cells, groupCol, groupIdx));
      if (raw === "") {
        group = lastGroup;
        isContinuation = lastGroup !== null;
      } else {
        isContinuation = normKey(raw) === normKey(lastGroup || "");
        group = raw;
        lastGroup = raw;
      }
    }

    spec.columns.forEach((col, colIdx) => {
      const cell = cellAt(cells, col, colIdx);
      const { value, problem } = checkCell(col, cell);
      values[col.key] = value;
      raw[col.key] =
        cell instanceof Date ? cell.toLocaleDateString("en-GB") : txt(cell);

      // On a continuation row the record-level columns belong to the row above.
      const applies = !isContinuation || col.scope !== "record";
      if (problem) problems.push(problem);
      else if (col.mandatory && applies && (value === null || value === ""))
        problems.push(`${col.header} is missing (column ${col.letter})`);
    });

    return { rowNo, values, raw, group, isContinuation, problems };
  });
};

/** Headline numbers for the pre-flight tiles. */
export const summarise = (rows, spec) => {
  const blocked = rows.filter((r) => r.problems.length).length;
  const groups = spec.groupBy
    ? new Set(rows.map((r) => normKey(r.group || `#${r.rowNo}`))).size
    : rows.length;
  return { total: rows.length, ready: rows.length - blocked, blocked, groups };
};

/** The problem rows, as a downloadable sheet to fix and re-upload. */
export const downloadProblemReport = (rows, spec) => {
  const headers = headersOf(spec);
  const aoa = [
    ["Excel Row", "What is wrong", ...headers],
    ...rows.map((r) => [
      r.rowNo,
      r.problems.join(" | "),
      ...spec.columns.map((c) => {
        const v = r.values[c.key];
        if (v === true) return "Yes";
        if (v === false) return "No";
        return v ?? "";
      }),
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 10 }, { wch: 70 }, ...autoFitCols([headers], 12, 30)];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Rows To Fix");
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    spec.fileName.replace(/_Template\.xlsx$/, "_Rows_To_Fix.xlsx")
  );
};
